/**
 * Rewrites the runtime entity ids of a serialized scene to compact, positional
 * ids — objects to `o0,o1,…`, emitters to `e0,e1,…` — and re-points every
 * connection `from` / `to` through the same map, so a saved / exported scene
 * carries short ids instead of 36-char UUIDs. Runs only at the save / export
 * boundary on already-serialized JSON; the live fields keep their UUIDs (the
 * clipboard, undo snapshots and per-edit localStorage all match on those), and
 * loading short ids back is transparent because an entity id is an opaque
 * string. Entries without a string id, and connection endpoints not in the map
 * (e.g. a dangling reference), are passed through unchanged. A connection's own
 * `id` is dropped entirely — nothing references it and the loader regenerates
 * one on parse — so the edge carries only its from/to/port/param.
 */
export interface RemappedScene {
  objects: unknown;
  emitters: unknown;
  connections: unknown;
}

function buildIdMap(entries: unknown, prefix: string, map: Map<string, string>): void {
  if (!Array.isArray(entries)) {
    return;
  }
  let i = 0;
  for (const entry of entries) {
    if (typeof entry === "object" && entry !== null) {
      const id = (entry as Record<string, unknown>).id;
      if (typeof id === "string" && !map.has(id)) {
        map.set(id, `${prefix}${i}`);
        i++;
      }
    }
  }
}

function remapIds(entries: unknown, map: Map<string, string>): unknown {
  if (!Array.isArray(entries)) {
    return entries;
  }
  return entries.map((entry: unknown): unknown => {
    if (typeof entry !== "object" || entry === null) {
      return entry;
    }
    const e = entry as Record<string, unknown>;
    const id = e.id;
    if (typeof id !== "string") {
      return entry;
    }
    const short = map.get(id);
    return short === undefined ? entry : { ...e, id: short };
  });
}

function remapConnections(entries: unknown, map: Map<string, string>): unknown {
  if (!Array.isArray(entries)) {
    return entries;
  }
  return entries.map((entry: unknown): unknown => {
    if (typeof entry !== "object" || entry === null) {
      return entry;
    }
    const e = entry as Record<string, unknown>;
    const out: Record<string, unknown> = { ...e };
    delete out.id;
    if (typeof e.from === "string" && map.has(e.from)) {
      out.from = map.get(e.from);
    }
    if (typeof e.to === "string" && map.has(e.to)) {
      out.to = map.get(e.to);
    }
    return out;
  });
}

export function remapSceneIds(objects: unknown, emitters: unknown, connections: unknown): RemappedScene {
  const map = new Map<string, string>();
  buildIdMap(objects, "o", map);
  buildIdMap(emitters, "e", map);
  return {
    objects: remapIds(objects, map),
    emitters: remapIds(emitters, map),
    connections: remapConnections(connections, map),
  };
}
